import ProductFlowLayout from "@/layouts/product-flow"
import Button from "@/components/button"
import SpinnerCircle from "@/components/spinner"
import Topbar from "@/components/topbar"
import { Skeleton } from "@/components/ui/skeleton"
import { useWidgetRegistry } from "@/lib/form/hooks"
import DatePickerWidget from "@/lib/form/widgets/date-picker"
import DropdownWidget from "@/lib/form/widgets/dropdown"
import { FormView } from "@/lib/form/view"
import useStore from "@/store"
import {
  ProductTabIndicator,
  ProductTabs,
  ProductTabsContent,
  ProductTabsList,
  ProductTabsTrigger,
} from "@/components/product-tabs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  FormData as IpqcFormData,
  FormParamStatus,
  FormStatus,
  isSubmittedLikeStatus,
} from "@/lib/form/types"
import {
  clearOfflineIpqcSessionState,
  checkHandover,
  useSyncSheet,
  createOfflineSessionSnapshot,
  deleteSheet,
  discardShiftNew,
  endShiftNew,
  getSessionSheets,
  getIPQCProductsForShift,
  hydrateOnlineIpqcSession,
  type SheetResponse,
  syncCurrentOfflineIpqcSession,
  syncSubmittedOfflineIpqcHandovers,
  submitAllSheetsNew,
  SuggestedEmployee,
} from "@/services/ipqc"
import EndShiftSheet, { PendingItem } from "@/components/end-shift-sheet"
import { useNavigate } from "react-router"
import { HTTPError } from "ky"
import { toast } from "sonner"
import AddProductsDialog from "@/ipqc/components/add-products-dialog"
import { useIPQCForm } from "@/hooks/ipqc"
import { useEnsureIpqcOfflineBundle } from "@/hooks/use-ensure-ipqc-offline-bundle"
import { useFieldStatus, UseFormReturn, useWatch } from "@/lib/form/form"
import { useDebounceByHash, useDisableBack } from "@/lib/utils"
import {
  handleNetworkError,
  handleOfflineActionError,
  isNetworkUnavailableError,
} from "@/services/base"
import { HandoverBar } from "@/components/handover-bar"
import { FormSchema, HandoverFormSchema } from "@/lib/form/schema"
import { ShiftDialog, toLineStatusKey, toLineStatusKeyParts } from "@/store/ipqc"
import { usePostHog } from "posthog-js/react"
import { getFormFilledStatus } from "@/ipqc/lib/utils"
import { saveOfflineIpqcSession } from "@/ipqc/lib/offline-store"
import ConfirmActionSheet from "@/components/confirm-action-sheet"
import type { AutoSaveStatus } from "@/rmpm/lib/auto-save-indicator"
import { IPQC_OFFLINE_BUNDLE_OFFLINE_MESSAGE } from "@/services/ipqc"

const Sheets = {
  EndShift: "EndShift",
  DiscardShift: "DiscardShift",
  DeleteLine: "DeleteLine",
  ConfirmSubmit: "ConfirmSubmit",
  None: "None",
} as const

export default function DoShiftPage() {
  const posthog = usePostHog()
  const debounce = useDebounceByHash(500)
  const navigate = useNavigate()
  const registry = useWidgetRegistry({
    DATE: DatePickerWidget,
    DROPDOWN: DropdownWidget,
  })

  const ipqc = useStore.use.ipqc()
  const sessionStore = useStore.use.session()
  const setFormData = useStore.use.ipqc().setSelfFormData
  const setLineStatus = useStore.use.ipqc().setLineStatus
  const setSubmitLoading = useStore.use.ipqc().setSubmitLoading

  const currLineIdx = useStore.use.ipqc().currLineIdx
  const setCurrLineIdx = useStore.use.ipqc().setCurrLineIdx

  const currLineStatus = useStore.use.ipqc().getCurrLineStatus()

  const currProdIdx = useStore.use.ipqc().currProductIdx
  const setCurrProductIdx = useStore.use.ipqc().setCurrProductIdx
  const currProdId = ipqc.currProdId

  const schemas = useStore.use.ipqc().schemas
  const forms = useStore.use.ipqc().forms

  const handovers = useStore.use.ipqc().schemas.handovers
  const currHandoverSessionId = useStore.use.ipqc().currHandoverSessionId
  const currHandoverIdx = useStore.use.ipqc().currHandoverIdx
  const setCurrHandoverIdx = useStore.use.ipqc().setCurrHandoverIdx
  const setHandoverFormData = useStore.use.ipqc().setHandoverFormData
  const setSelfFormStatus = useStore.use.ipqc().setSelfFormStatus
  const setHandoverFormStatus = useStore.use.ipqc().setHandoverFormStatus
  const isSyncEnabled = !ipqc.submitLoading
  const [dataVersion, setDataVersion] = useState(0)

  const { form, schema } = useActiveForm()
  const formStatus = useFieldStatus({
    form,
    name: `products[${currProdIdx}].productionLines[${currLineIdx}]`,
    fieldOnly: !currHandoverSessionId,
  })
  const currentLineMachines = useWatch({
    form,
    name: `products[${currProdIdx}].productionLines[${currLineIdx}].machines`,
    defaultValue: [],
    deep: true,
  })

  const isLineReadOnly = isSubmittedLikeStatus(currLineStatus)
  const hasActiveBreakdown = useMemo(() => {
    if (!Array.isArray(currentLineMachines)) {
      return false
    }

    return currentLineMachines.some((machine) =>
      machine?.breakdown?.some(
        (breakdown: { endedAt?: string }) => !breakdown?.endedAt
      )
    )
  }, [currentLineMachines])

  const [sheetState, setSheetState] = useState("")
  const [handoverEmployees, setHandoverEmployees] = useState<
    SuggestedEmployee[]
  >([])
  const [handoverPendingItems, setHandoverPendingItems] = useState<
    PendingItem[]
  >([])
  const [handoverSheetIds, setHandoverSheetIds] = useState<string[]>([])
  const [isHandoverRequired, setIsHandoverRequired] = useState(false)
  const [isEndingProcess, setIsEndingProcess] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>("idle")
  const { ensureBundle: ensureOfflineBundle } = useEnsureIpqcOfflineBundle({
    enabled: ipqc.offline.hydrated && !!ipqc.shift.processId,
  })

  const refreshAfterBreakdownMutation = useCallback(async () => {
    setDataVersion((v) => v + 1)
  }, [])

  useDisableBack(!!schema)
  const { isLoading, error, info } = useEnsureDataLoaded()
  useInit()
  const showEndProcess = useShowEndProcess()
  const hasProducts = !!schema?.products?.length
  const canDeleteCurrentLine =
    !currHandoverSessionId &&
    !ipqc.isReadOnly &&
    !isLineReadOnly &&
    currProdIdx >= 0 &&
    currLineIdx >= 0 &&
    !!schema?.products.at(currProdIdx)?.productionLines.at(currLineIdx)

  function getCurrentLineSchema() {
    return schema?.products.at(currProdIdx)?.productionLines.at(currLineIdx)
  }

  function getCurrentServerSheetId(): string | null {
    return getCurrentLineSchema()?.serverSheetId || null
  }

  useEffect(() => {
    if (autoSaveStatus === "saved") {
      const timer = setTimeout(() => {
        setAutoSaveStatus("idle")
      }, 1200)

      return () => clearTimeout(timer)
    }

    if (autoSaveStatus === "error") {
      const timer = setTimeout(() => {
        setAutoSaveStatus("idle")
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [autoSaveStatus])

  function useEnsureDataLoaded() {
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [info] = useState<string | null>(null)
    const loadingRef = useRef(false)

    useEffect(() => {
      async function loadData() {
        if (!ipqc.offline.hydrated) {
          return
        }

        // Prevent duplicate requests
        if (loadingRef.current) {
          return
        }

        loadingRef.current = true
        setIsLoading(true)
        setError(null)

        try {
          let freshIpqc = useStore.getState().ipqc

          if (freshIpqc.offline.activeSession && !freshIpqc.isReadOnly) {
            setDataVersion((v) => v + 1)
            return
          }

          if (
            freshIpqc.isReadOnly &&
            freshIpqc.shift.processId &&
            typeof navigator !== "undefined" &&
            navigator.onLine
          ) {
            const shiftStartTime = sessionStore.shift.startedAt || undefined
            const shiftEndTime = sessionStore.shift.endedAt || undefined
            const hydratedSession = await hydrateOnlineIpqcSession(
              shiftStartTime,
              shiftEndTime,
              { persistOfflineSession: false }
            )

            if (hydratedSession?.sessionId) {
              setDataVersion((v) => v + 1)
              return
            }
          }

          const bundle =
            freshIpqc.offline.bundle ||
            (freshIpqc.shift.processId ? await ensureOfflineBundle() : null)

          freshIpqc = useStore.getState().ipqc

          if (!freshIpqc.shift.products.length && bundle) {
            await getIPQCProductsForShift()
            freshIpqc = useStore.getState().ipqc
          }

          if (bundle && freshIpqc.shift.processId) {
            await navigate("/ipqc/select-products")
            return
          }

          setError(IPQC_OFFLINE_BUNDLE_OFFLINE_MESSAGE)
        } catch (err) {
          console.error("Failed to load data:", err)
          posthog.captureException(err)

          const handled = await handleNetworkError(err, navigate)
          if (!handled) {
            setError(
              err instanceof Error ? err.message : "Failed to load IPQC data"
            )
            toast.error("Failed to load shift data")
          }
        } finally {
          setIsLoading(false)
          loadingRef.current = false
        }
      }

      void loadData()
    }, [ensureOfflineBundle, ipqc.offline.hydrated, ipqc.isReadOnly])

    return { isLoading, error, info }
  }

  function useInit() {
    useSyncSheet({
      form,
      schema,
      enabled: isSyncEnabled,
      onStatusChange: setAutoSaveStatus,
    })
    useSyncFormToStore()

    useEffect(() => {
      form.addEventHandler("refreshAfterBreakdownMutation", async () => {
        await refreshAfterBreakdownMutation()
      })

      return () => {
        form.removeEventHandler("refreshAfterBreakdownMutation")
      }
    }, [form, refreshAfterBreakdownMutation])
  }

  function useActiveForm() {
    const [schema, setSchema] = useState<FormSchema | HandoverFormSchema>()
    const { form, rebuildForm } = useIPQCForm({
      schema: schema,
      isHandover: !!(schema as any)?.handoverFromEmployeeCode,
    })

    function updateActiveForm(params: { handoverIdx: number }) {
      // Read fresh state from the store to avoid stale closure issues on refresh
      const freshIpqc = useStore.getState().ipqc
      const freshSchemas = freshIpqc.schemas
      const freshForms = freshIpqc.forms

      if (params.handoverIdx > -1) {
        const handoverSchema = freshSchemas.handovers[params.handoverIdx]
        const handoverForm = freshForms.handovers[params.handoverIdx]

        if (!handoverSchema || !handoverForm) {
          setCurrHandoverIdx(-1)
          setSchema(freshSchemas.self)
          rebuildForm({
            schema: freshSchemas.self,
            defaultValues: freshForms.self,
            isHandover: false,
          })

          return
        }

        setSchema(handoverSchema)
        rebuildForm({
          schema: handoverSchema,
          defaultValues: handoverForm,
          isHandover: true,
        })
      } else {
        setSchema(freshSchemas.self)
        rebuildForm({
          schema: freshSchemas.self,
          defaultValues: freshForms.self,
          isHandover: false,
        })
      }
    }

    useEffect(() => {
      updateActiveForm({
        handoverIdx: currHandoverIdx,
      })
    }, [currHandoverIdx, schemas, dataVersion])

    return {
      form,
      schema,
    }
  }

  function useSyncFormToStore() {
    function sync() {
      sessionStore.setProcessUpdatedAt(
        ipqc.shift.processId || "",
        new Date().toISOString()
      )
      const formValues = form.getValues()

      if (currHandoverSessionId) {
        const activeHandoverSchema = schemas.handovers[currHandoverIdx]
        if (!activeHandoverSchema) {
          return
        }

        setHandoverFormData(
          {
            handoverFromEmployeeSessionId: currHandoverSessionId,
            handoverFromEmployeeCode:
              activeHandoverSchema.handoverFromEmployeeCode,
            ...formValues,
          },
          currHandoverSessionId
        )

        return
      }

      setFormData(formValues)
    }

    useEffect(() => {
      form.addEventHandler("change", () => {
        debounce("syncFormToStore", sync)
      })

      return () => {
        form.removeEventHandler("change")
      }
    }, [form, currHandoverSessionId, currHandoverSessionId, currHandoverIdx])

    useEffect(() => {
      const status = form.getFieldStatus()

      if (currHandoverSessionId) {
        setHandoverFormStatus(currHandoverSessionId, status)
      } else {
        setSelfFormStatus(status)
      }
    }, [formStatus, currHandoverSessionId])
  }

  const productIndicators = useProductIndicators()

  function useProductIndicators(): ProductTabIndicator[] {
    const [indicators, setIndicators] = useState<ProductTabIndicator[]>([])

    function getIndicatorsOfForm(
      schema: FormSchema | undefined | null,
      filled: Record<string, boolean>,
      lineStatus: Record<string, FormParamStatus | undefined>,
      sessionId: string
    ): ProductTabIndicator[] {
      if (!schema) {
        return []
      }

      const statuses = Object.entries(filled).reduce(
        (acc, [key, isCurrFilled]) => {
          const [prodIdx, lineIdx] = key.split("-").slice(0, 2).map(Number)
          const product = schema.products[prodIdx]
          const line = product?.productionLines[lineIdx]

          if (!isCurrFilled || !product || !line) {
            return acc
          }

          const lineStatusKey = toLineStatusKey({
            productId: product.id,
            lineId: line.id,
            sessionId,
          })

          if (!acc[prodIdx] || isLineSubmittedOrCompleted(acc[prodIdx])) {
            acc[prodIdx] = lineStatus[lineStatusKey] || FormParamStatus.PENDING
          }

          return acc
        },
        {} as Record<number, FormParamStatus | undefined>
      )

      return schema.products.map((__, idx) => {
        if (isLineSubmittedOrCompleted(statuses[idx])) {
          return ProductTabIndicator.Success
        }

        if (statuses[idx]) {
          return ProductTabIndicator.Warning
        }

        return ProductTabIndicator.None
      })
    }

    useEffect(() => {
      const handoversFilled = ipqc.forms.handovers.map((handover) => {
        const filled = getFormFilledStatus(handover)
        return {
          sessionId: handover.handoverFromEmployeeSessionId || "",
          filled,
        }
      })
      ipqc.setHandoversFilled(handoversFilled)

      const selfFilled = getFormFilledStatus(ipqc.forms.self)
      ipqc.setSelfFilled(selfFilled)
    }, [forms])

    useEffect(() => {
      if (currHandoverSessionId && currHandoverIdx >= 0) {
        const handoverFilledData = ipqc.filled.handovers[currHandoverIdx]
        if (!handoverFilledData) {
          return
        }
        const handoverFilled = handoverFilledData.filled
        const newIndicators = getIndicatorsOfForm(
          schema,
          handoverFilled,
          ipqc.lineStatus,
          currHandoverSessionId
        )
        setIndicators(newIndicators)

        return
      }

      const newIndicators = getIndicatorsOfForm(
        schema,
        ipqc.filled.self,
        ipqc.lineStatus,
        "self"
      )

      setIndicators(newIndicators)
    }, [ipqc.filled, schema, ipqc.lineStatus])

    return indicators
  }

  function useShowEndProcess() {
    const [canEnd, setCanEnd] = useState(false)

    useEffect(() => {
        if (ipqc.offline.activeSession) {
          setCanEnd(true)
          return
        }

      for (const [productIdx, product] of ipqc.forms.self.products.entries()) {
        const schemaProduct = ipqc.schemas.self.products[productIdx]
        for (const [lineIdx] of product.productionLines.entries()) {
          const schemaLine = schemaProduct?.productionLines[lineIdx]
          if (
            schemaProduct &&
            schemaLine &&
            isLineSubmittedOrCompleted(
              ipqc.lineStatus[
                toLineStatusKey({
                  productId: schemaProduct.id,
                  lineId: schemaLine.id,
                })
              ]
            )
          ) {
            setCanEnd(true)

            return
          }
        }
      }

      for (const [handoverIdx, handover] of ipqc.forms.handovers.entries()) {
        const handoverSchema = ipqc.schemas.handovers[handoverIdx]
        for (const [productIdx, product] of handover.products.entries()) {
          const schemaProduct = handoverSchema?.products[productIdx]
          for (const [lineIdx] of product.productionLines.entries()) {
            const schemaLine = schemaProduct?.productionLines[lineIdx]
            if (
              schemaProduct &&
              schemaLine &&
              isLineSubmittedOrCompleted(
                ipqc.lineStatus[
                  toLineStatusKey({
                    productId: schemaProduct.id,
                    lineId: schemaLine.id,
                    sessionId: handover.handoverFromEmployeeSessionId,
                  })
                ]
              )
            ) {
              setCanEnd(true)

              return
            }
          }
        }
      }

      setCanEnd(false)
    }, [ipqc.forms, ipqc.lineStatus])

    return canEnd
  }

  function setFieldsToSubmitted(form: UseFormReturn) {
    if (!schema) {
      return
    }

    const preserveStatuses: FormParamStatus[] = [
      FormParamStatus.SUBMITTED,
      FormParamStatus.NOT_APPLICABLE,
      FormParamStatus.BREAK_DOWN,
      FormParamStatus.CHANGE_OVER,
      FormParamStatus.POWER_FAILURE,
      FormParamStatus.OTHER,
    ]

    for (const [prodIdx, product] of schema.products.entries()) {
      const prodPath = `products[${prodIdx}]`

      if (prodIdx !== currProdIdx) {
        continue
      }

      for (const [lineIdx, line] of product.productionLines.entries()) {
        const linePath = `${prodPath}.productionLines[${lineIdx}]`

        if (lineIdx !== currLineIdx) {
          continue
        }

        for (const [stageIdx, stage] of line.machines.entries()) {
          const stagePath = `${linePath}.machines[${stageIdx}]`

          for (const [procIdx, proc] of stage.internalProcesses.entries()) {
            const procPath = `${stagePath}.internalProcesses[${procIdx}]`

            for (const [paramIdx] of proc.parameters.entries()) {
              const paramPath = `${procPath}.parameters[${paramIdx}]`

              const value = form.getValues(paramPath)

              if (value?.window?.length) {
                for (const [index] of value.window.entries()) {
                  const path = `${paramPath}.window[${index}]`
                  const value = form.getValues(path)

                  if (
                    !value?.status ||
                    !preserveStatuses.includes(value?.status)
                  ) {
                    form.setValue(`${path}.status`, FormParamStatus.SUBMITTED)
                  }
                }
              } else {
                const value = form.getValues(paramPath)

                if (
                  !value?.status ||
                  !preserveStatuses.includes(value?.status)
                ) {
                  form.setValue(
                    `${paramPath}.status`,
                    FormParamStatus.SUBMITTED
                  )
                }
              }
            }
          }
        }
      }
    }
  }

  function isRecoverableNetworkSubmitError(error: unknown): boolean {
    return (
      isNetworkUnavailableError(error) ||
      (error instanceof Error && error.name === "AbortError")
    )
  }

  function getSubmitErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message
    }

    return "Error saving form locally"
  }

  async function handleSubmit() {
    const activeSessionId = currHandoverSessionId || "self"
    const productId = ipqc.currProdId()
    const lineId = ipqc.currLineId()
    const currentServerSheetId = getCurrentServerSheetId()

    try {
      setSubmitLoading(true)
      if (schema) {
        setFieldsToSubmitted(form)

        if (currHandoverSessionId) {
          ipqc.setHandoverFormData(form.getValues(), currHandoverSessionId)
        } else {
          ipqc.setSelfFormData(form.getValues())
        }
      }

      if (navigator.onLine && schema && currentServerSheetId) {
        const submittingLines = new Set<string>()
        const lineStatusKey = toLineStatusKey({
          productId,
          lineId,
          sessionId: activeSessionId,
        })
        submittingLines.add(lineStatusKey)

        await submitAllSheetsNew({
          form: form.getValues(),
          schema,
          lineStatus: ipqc.lineStatus,
          sessionId: activeSessionId,
          submittingLines,
        })
      }

      setLineStatus({
        productId,
        lineId,
        status: FormStatus.SUBMITTED,
        sessionId: currHandoverSessionId,
      })

      const isOffline =
        typeof navigator !== "undefined" && navigator.onLine === false

      if (ipqc.offline.activeSession && (isOffline || !currentServerSheetId)) {
        ipqc.updateOfflineSessionMeta({
          isDirty: true,
          hasDeferredSync:
            ipqc.offline.activeSession.hasDeferredSync || !currentServerSheetId,
        })
        await saveOfflineIpqcSession(createOfflineSessionSnapshot())
      }
    } catch (err) {
      if (isRecoverableNetworkSubmitError(err) && schema) {
        setLineStatus({
          productId,
          lineId,
          status: FormStatus.SUBMITTED,
          sessionId: currHandoverSessionId,
        })

        if (ipqc.offline.activeSession) {
          ipqc.updateOfflineSessionMeta({
            isDirty: true,
            hasDeferredSync:
              ipqc.offline.activeSession.hasDeferredSync || !currentServerSheetId,
          })
          await saveOfflineIpqcSession(createOfflineSessionSnapshot())
        }

        toast.success("Sheet saved offline and will sync later")
        return
      }

      posthog.captureException(err, {
        schema: schema,
        data: form.getValues(),
      })
      toast.error(getSubmitErrorMessage(err))
      console.error(err)
    } finally {
      setSubmitLoading(false)
      handleSheetChange(Sheets.ConfirmSubmit, false)
    }
  }

  function handleSheetChange(sheet: string, open: boolean) {
    if (open) {
      setSheetState(sheet)
    } else {
      setSheetState("")
    }
  }

  function isLineSubmittedOrCompleted(status?: FormParamStatus): boolean {
    return isSubmittedLikeStatus(status)
  }

  function isParamPending(status?: FormParamStatus): boolean {
    if (!status) {
      return true
    }

    return (
      status === FormParamStatus.PENDING ||
      status === FormParamStatus.DRAFT
    )
  }

  function isBlockingEndProcessStatus(status?: FormParamStatus): boolean {
    if (!status) {
      return true
    }

    return (
      status === FormParamStatus.PENDING ||
      status === FormParamStatus.DRAFT
    )
  }

  function getBlockingEndProcessSheets(
    draftProducts: FormSchema["products"],
    lineStatuses: Record<string, FormParamStatus | undefined>
  ): string[] {
    return draftProducts.flatMap((product) =>
      product.productionLines.flatMap((line) => {
        const status =
          lineStatuses[toLineStatusKey({ productId: product.id, lineId: line.id })] ||
          line.status

        if (!isBlockingEndProcessStatus(status)) {
          return []
        }

        return [`${product.name} - ${line.name}`]
      })
    )
  }

  function getBlockingDraftHandoverSheets(
    handovers: HandoverFormSchema[],
    lineStatuses: Record<string, FormParamStatus | undefined>
  ): string[] {
    return handovers.flatMap((handover) =>
      handover.products.flatMap((product) =>
        product.productionLines.flatMap((line) => {
          const status =
            lineStatuses[
              toLineStatusKey({
                productId: product.id,
                lineId: line.id,
                sessionId: handover.handoverFromEmployeeSessionId,
              })
            ] || line.status

          if (!isBlockingEndProcessStatus(status)) {
            return []
          }

          return [`${product.name} - ${line.name}`]
        })
      )
    )
  }

  function mapPendingItemsFromDraftSchema(
    draftProducts: FormSchema["products"],
    pendingLineIds: string[]
  ): PendingItem[] {
    if (!pendingLineIds.length) {
      return []
    }

    const pendingLineIdSet = new Set(pendingLineIds)

    return draftProducts.flatMap((product) =>
      product.productionLines.flatMap((line) => {
        if (!pendingLineIdSet.has(line.id)) {
          return []
        }

        const subItems = line.machines.flatMap((stage) => {
          const pendingParams = stage.internalProcesses.flatMap((proc) =>
            proc.parameters
              .filter((param) => {
                if (param.window?.length) {
                  return param.window.some((windowItem) =>
                    isParamPending(windowItem.status)
                  )
                }

                return isParamPending(param.status)
              })
              .map((param) => param.name)
          )

          if (!pendingParams.length) {
            return []
          }

          return [`${stage.name}: ${pendingParams.join(", ")}`]
        })

        return [
          {
            label: `${product.name} • ${line.name}`,
            subItems: subItems.length
              ? subItems
              : ["Pending readings will be carried forward for handover."],
          },
        ]
      })
    )
  }

  function mapPendingItemsFromSessionSheets(
    sheets: SheetResponse[],
    pendingSheetIds: string[]
  ): PendingItem[] {
    if (!pendingSheetIds.length) {
      return []
    }

    const pendingSheetIdSet = new Set(pendingSheetIds)

    return sheets
      .filter((sheet) => pendingSheetIdSet.has(sheet.id))
      .map((sheet) => ({
        label: `${sheet.productName} • ${sheet.lineName}`,
        subItems: ["Pending readings will be carried forward for handover."],
      }))
  }

  function lineHasActiveBreakdown(formData: IpqcFormData, lineId: string) {
    const line = formData.products
      .flatMap((product) => product.productionLines)
      .find((item) => item.id === lineId)

    return (
      line?.machines.some((machine) =>
        machine.breakdown?.some((breakdown) => !breakdown.endedAt)
      ) ?? false
    )
  }

  async function handleTriggerEndShift() {
    try {
      setIsEndingProcess(true)
      if (!navigator.onLine) {
        handleOfflineActionError(new TypeError("Network unavailable"), {
          action: "end",
          target: "process",
        })
        return
      }

      const blockingSheets = getBlockingEndProcessSheets(
        useStore.getState().ipqc.schemas.self.products,
        useStore.getState().ipqc.lineStatus
      )

      if (blockingSheets.length) {
        const previewSheets = blockingSheets.slice(0, 2).join(", ")
        const extraCount = blockingSheets.length - 2
        const message =
          blockingSheets.length === 1
            ? `Submit or delete the pending sheet before ending the process: ${blockingSheets[0]}`
            : `Submit or delete all pending sheets before ending the process: ${previewSheets}${
                extraCount > 0 ? ` and ${extraCount} more` : ""
              }`

        toast.error(message)
        return
      }

      const blockingHandoverSheets = getBlockingDraftHandoverSheets(
        useStore.getState().ipqc.schemas.handovers,
        useStore.getState().ipqc.lineStatus
      )

      if (blockingHandoverSheets.length) {
        const previewSheets = blockingHandoverSheets.slice(0, 2).join(", ")
        const extraCount = blockingHandoverSheets.length - 2
        const message =
          blockingHandoverSheets.length === 1
            ? `Submit the received handover sheet before ending the process: ${blockingHandoverSheets[0]}`
            : `Submit all received handover sheets before ending the process: ${previewSheets}${
                extraCount > 0 ? ` and ${extraCount} more` : ""
              }`

        toast.error(message)
        return
      }

      let pendingSheetIds: string[] = []
      let pendingItems: PendingItem[] = []
      let handoverEmployees: SuggestedEmployee[] = []
      let hasPendingSheets = false
      const activeSession = useStore.getState().ipqc.offline.activeSession
      const shouldUseLocalDraftState = !navigator.onLine

      if (
        navigator.onLine &&
        (activeSession?.isDirty || activeSession?.hasDeferredSync)
      ) {
        await syncCurrentOfflineIpqcSession()
        await syncSubmittedOfflineIpqcHandovers()
      }

      if (shouldUseLocalDraftState) {
        const currentIpqc = useStore.getState().ipqc
        const pendingLineIds = Object.entries(currentIpqc.lineStatus)
          .filter(([key, status]) => {
            const { lineId, sessionId } = toLineStatusKeyParts(key)

            return (
              sessionId === "self" &&
              isBlockingEndProcessStatus(status) &&
              !lineHasActiveBreakdown(currentIpqc.forms.self, lineId)
            )
          })
          .map(([key]) => toLineStatusKeyParts(key).lineId)
        pendingSheetIds = pendingLineIds
        pendingItems = mapPendingItemsFromDraftSchema(
          currentIpqc.schemas.self.products,
          pendingLineIds
        )
        hasPendingSheets = pendingLineIds.length > 0

        const cachedBundleEmployees =
          useStore.getState().ipqc.offline.bundle?.employees
        handoverEmployees = Array.isArray(cachedBundleEmployees)
          ? cachedBundleEmployees
          : []

        if (
          hasPendingSheets &&
          !Array.isArray(cachedBundleEmployees) &&
          navigator.onLine
        ) {
          const refreshedBundle = await ensureOfflineBundle()
          handoverEmployees = refreshedBundle?.employees || []
        }
      } else {
        const handoverCheck = await checkHandover()
        pendingSheetIds = handoverCheck.pendingSheetIds
        const sessionSheets = await getSessionSheets()
        pendingItems = mapPendingItemsFromSessionSheets(
          sessionSheets.sheets,
          pendingSheetIds
        )
        handoverEmployees = handoverCheck.employees
        hasPendingSheets = handoverCheck.handoverRequired
      }

      if (hasPendingSheets && !handoverEmployees.length) {
        toast.error("No employees are available for handover")
        return
      }

      setHandoverSheetIds(pendingSheetIds)
      setHandoverPendingItems(pendingItems)
      setIsHandoverRequired(hasPendingSheets)
      setHandoverEmployees(hasPendingSheets ? handoverEmployees : [])
      setSheetState(Sheets.EndShift)
    } catch (err) {
      if (handleOfflineActionError(err, { action: "end", target: "process" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        return
      }

      posthog.captureException(err)
      console.error(err)
      toast.error("Error ending shift")
    } finally {
      setIsEndingProcess(false)
    }
  }

  async function handleEndShift(handoverEmployeeCode: string) {
    try {
      if (isHandoverRequired && !handoverEmployeeCode) {
        toast.error("Select an employee to hand over the pending sheets")
        return
      }

      if (!navigator.onLine) {
        handleOfflineActionError(new TypeError("Network unavailable"), {
          action: "end",
          target: "process",
        })
        return
      }

      let resolvedHandoverSheetIds = handoverSheetIds
      const activeSession = useStore.getState().ipqc.offline.activeSession

      if (activeSession?.isDirty || activeSession?.hasDeferredSync) {
        await syncCurrentOfflineIpqcSession({ handoverEmployeeCode })
        await syncSubmittedOfflineIpqcHandovers()

        const handoverCheck = await checkHandover()
        resolvedHandoverSheetIds = handoverCheck.pendingSheetIds
      }

      const response = await endShiftNew(
        handoverEmployeeCode || undefined,
        resolvedHandoverSheetIds
      )
      if (!response.success) {
        toast.error(response.message || "Error ending shift")
        return
      }

      await clearOfflineIpqcSessionState()

      setSheetState("")
      await navigate("/manage-processes")
    } catch (err) {
      if (handleOfflineActionError(err, { action: "end", target: "process" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        setSheetState("")
        return
      }

      posthog.captureException(err)
      console.error(err)
      toast.error("Error ending shift")
    }
  }

  function handleTriggerDiscardShift() {
    setSheetState(Sheets.DiscardShift)
  }

  function handleTriggerDeleteLine() {
    setSheetState(Sheets.DeleteLine)
  }

  async function handleDeleteLine() {
    const applyLocalDelete = async () => {
      ipqc.removeSelfProductionLine({
        productIdx: currProdIdx,
        lineIdx: currLineIdx,
      })

      if (ipqc.offline.activeSession) {
        ipqc.updateOfflineSessionMeta({
          isDirty: true,
          hasDeferredSync: true,
        })
        await saveOfflineIpqcSession(createOfflineSessionSnapshot())
      }

      setSheetState(Sheets.None)
    }

    try {
      if (!canDeleteCurrentLine) {
        toast.error("Only new or draft sheets can be deleted")
        setSheetState(Sheets.None)
        return
      }

      const sheetId = getCurrentServerSheetId()
      const isServerBackedSession =
        !ipqc.offline.activeSession ||
        !!ipqc.offline.activeSession.serverSessionId

      if (navigator.onLine && isServerBackedSession && sheetId) {
        await deleteSheet(sheetId)
      }

      await applyLocalDelete()
    } catch (err) {
      if (
        ipqc.offline.activeSession &&
        isRecoverableNetworkSubmitError(err)
      ) {
        await applyLocalDelete()
        toast.error(
          "Backend unreachable. Sheet deleted locally and will sync later."
        )
        return
      }

      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        return
      }

      console.error(err)
      toast.error("Error deleting sheet")
      posthog.captureException(err)
    }
  }

  async function handleDiscardShift() {
    try {
      await discardShiftNew()
      await navigate("/manage-processes")
    } catch (err) {
      if (
        handleOfflineActionError(err, { action: "discard", target: "process" })
      ) {
        return
      }

      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        return
      }

      console.error(err)
      toast.error("Error discarding shift")
      posthog.captureException(err)
    }
  }

  function handleProductChange(value: string) {
    const newProductIdx = schema?.products.findIndex(
      (item) => item.id === value
    )

    if (typeof newProductIdx !== "number" || newProductIdx < 0) {
      return
    }

    setCurrProductIdx(newProductIdx)
    setCurrLineIdx(0)
  }

  function handleSwitchToHandover(
    checked: boolean,
    __sessionId: string,
    idx: number
  ) {
    if (checked) {
      setCurrHandoverIdx(idx)
    } else {
      setCurrHandoverIdx(-1)
    }

    setCurrProductIdx(0)
    setCurrLineIdx(0)
  }

  // Loading State
  if (isLoading) {
    return (
      <div className="h-dvh">
        <Topbar
          title="IPQC Product"
          showBack
          onClickBack={() => navigate("/manage-processes")}
        />
        <div
          className="h-1 w-full overflow-hidden bg-muted"
          role="progressbar"
          aria-label="Loading IPQC form data"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext="Loading"
        >
          <div className="ipqc-indeterminate-bar h-full w-1/3 bg-blue" />
        </div>
        <div className="px-4 pt-3 pb-22">
          <Skeleton className="h-13 w-full rounded-md" />

          <div className="mt-4 space-y-4">
            <div className="rounded-lg bg-white p-4 shadow-xs space-y-3">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-11 w-full rounded-lg" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-11 w-full rounded-lg" />
            </div>

            <div className="rounded-lg bg-white p-4 shadow-xs space-y-3">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>

            <div className="rounded-lg bg-white p-4 shadow-xs space-y-3">
              <Skeleton className="h-4 w-44" />
              <Skeleton className="h-11 w-full rounded-lg" />
              <Skeleton className="h-11 w-3/4 rounded-lg" />
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 border-t border-input-border bg-white p-4">
          <Skeleton className="h-11 w-full rounded-lg" />
        </div>
      </div>
    )
  }

  // Error State - Error Message with Go Back Button
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center flex flex-col gap-4 max-w-md px-4">
          <div className="text-error text-lg font-medium">{error}</div>
          <button
            onClick={() => navigate("/manage-processes")}
            className="px-4 py-2 bg-button-primary text-white rounded"
          >
            Go Back to Processes
          </button>
        </div>
      </div>
    )
  }

  // Info State - Informational message (e.g. ended session with no sheets)
  if (info) {
    return (
      <div className="h-dvh">
        <Topbar
          title="IPQC Product"
          showBack
          onClickBack={() => navigate("/manage-processes")}
        />
        <div className="flex items-center justify-center min-h-[60dvh]">
          <div className="text-center flex flex-col gap-4 max-w-md px-4">
            <div className="text-label-text text-lg">{info}</div>
          </div>
        </div>
      </div>
    )
  }

  if (!(schema && form)) {
    return <div className="text-center">Schema not found!</div>
  }

  return (
    <ProductFlowLayout
      hideBottomActions={!hasProducts}
      isSubmitDisabled={
        formStatus !== FormParamStatus.VALID && !hasActiveBreakdown
      }
      status={currLineStatus}
      canAddProducts={!currHandoverSessionId}
      headerSyncStatus={autoSaveStatus}
      onClickDelete={handleTriggerDeleteLine}
      onClickSubmit={() => handleSheetChange(Sheets.ConfirmSubmit, true)}
      onClickEndShift={handleTriggerEndShift}
      onClickDiscardShift={handleTriggerDiscardShift}
      hideEndProcess={!showEndProcess}
      isDeleteHidden={!canDeleteCurrentLine}
    >
      {isEndingProcess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
          <SpinnerCircle variant="light" />
        </div>
      )}

      {!!handovers.length && (
        <HandoverBar
          employeeCode=""
          sessionId={handovers[0].handoverFromEmployeeSessionId}
          checked={!!currHandoverSessionId}
          onCheckedChange={(checked, sessionId) =>
            handleSwitchToHandover(checked, sessionId, 0)
          }
        />
      )}

      {!hasProducts ? (
        <div className="px-4 pt-6">
          <div className="rounded-lg bg-white p-5 text-center shadow-xs">
            <h2 className="text-lg font-semibold text-button-black">
              No Products Added
            </h2>
            <p className="mt-2 text-sm text-label-text">
              Add a product to create a new IPQC production line sheet.
            </p>
            {!currHandoverSessionId && (
              <Button
                className="mt-4"
                onClick={() => ipqc.setShiftDialog(ShiftDialog.AddProducts)}
              >
                Add Product
              </Button>
            )}
          </div>
        </div>
      ) : (
        <ProductTabs value={currProdId()} onValueChange={handleProductChange}>
          <ProductTabsList className="sticky top-[68px] z-10">
            {schema.products.map((product, index) => (
              <ProductTabsTrigger
                key={product.id}
                value={product.id}
                indicator={productIndicators[index]}
              >
                {product.name}
              </ProductTabsTrigger>
            ))}
          </ProductTabsList>

          <ProductTabsContent
            className="px-4 pt-3 pb-8 flex justify-center"
            value=""
          >
            <div>Please select a product</div>
          </ProductTabsContent>

          {schema.products.map((product) => (
            <ProductTabsContent
              key={product.id}
              className="px-4 pt-3 pb-8"
              value={product.id}
            >
              <FormView
                schema={schema}
                registry={registry}
                formSchema={schema}
                form={form}
                activeProductId={currProdId()}
                readOnly={isLineReadOnly}
                sheetHasActiveBreakdown={hasActiveBreakdown}
              />
            </ProductTabsContent>
          ))}
        </ProductTabs>
      )}

      <EndShiftSheet
        open={sheetState === Sheets.EndShift}
        pendingItems={handoverPendingItems}
        employees={isHandoverRequired ? handoverEmployees : []}
        description="You are ending your IPQC shift for today."
        pendingDescription="You are ending your IPQC shift for today. Please select a reason below"
        onOpenChange={(open) => {
          handleSheetChange(Sheets.EndShift, open)
          if (!open) {
            setHandoverEmployees([])
            setIsHandoverRequired(false)
            setHandoverPendingItems([])
          }
        }}
        onClickBack={() => {
          handleSheetChange(Sheets.EndShift, false)
        }}
        onClickConfirm={handleEndShift}
      />
      <EndShiftSheet
        open={sheetState === Sheets.DiscardShift}
        title="Discard Process"
        description="Are you sure you want to Discard and Exit? This will permanently delete your current IPQC data."
        onClickBack={() => setSheetState(Sheets.None)}
        onClickConfirm={handleDiscardShift}
      />
      <AddProductsDialog />
      <ConfirmActionSheet
        open={sheetState === Sheets.DeleteLine}
        title="Delete Sheet"
        description="Are you sure you want to delete this production line sheet? This only removes the current sheet from the product."
        confirmLabel="Delete"
        onClose={() => handleSheetChange(Sheets.DeleteLine, false)}
        onClickBack={() => handleSheetChange(Sheets.DeleteLine, false)}
        onClickConfirm={handleDeleteLine}
      />
      <ConfirmActionSheet
        open={sheetState === Sheets.ConfirmSubmit}
        onClose={() => handleSheetChange(Sheets.ConfirmSubmit, false)}
        onClickBack={() => handleSheetChange(Sheets.ConfirmSubmit, false)}
        onClickConfirm={handleSubmit}
      />
    </ProductFlowLayout>
  )
}
